import { mapState as rootMapState } from 'pinia';
import { mapState } from '@/store/helpers';
import { usePageStore, useUserStore } from '@/store';
import util from '../../common/util';
import global from '../../common/global';
import Latlng from './js/latlng';
import Service from './js/service';
import ToolMapType from '../map/tools/map-type.vue';
import ToolZoom from './tools/zoom.vue';
import Nearby from './tools/nearby.vue';
import Locate from './tools/locate.vue';
import Street from './tools/street.vue';
import Search from '../map/tools/search.vue';
import Fullscreen from './tools/fullscreen.vue';
import Icon from './tools/icon.vue';
import MarkerAggre from './markers/aggre.vue';
import MarkerIcon from './markers/icon.vue';
import MarkerHouseBroker from './markers/house-broker.vue';
import MarkerHouseLayout from '../map/markers/house-layout.vue';
import MarkerHouse from './markers/house.vue';
import MarkerSchool from './markers/school.vue';
import MarkerSchoolLayout from '../map/markers/school-layout.vue';
import MarkerCity from './markers/city.vue';
import MapMask from '../map-mask.vue';
import mapUtil from './js/mapUtil';
import { defineAsyncComponent } from 'vue';
import { getBoundaryColor } from '../map/common';
import { getMarkers } from '../map/js/markers';

export default {
    emits: ['change', 'mapLoad'],
    inject: {
        showSearchBar: {
            type: Boolean,
            default: false
        },
        setApplyMap: {
            type: Function,
            default() {
                return () => {};
            }
        }
    },
    provide() {
        return {
            applyMap: this.applyMap,
            specialStyleManagement: () => this.specialStyleManagement,
            mapTheme: this.mapTheme
        };
    },
    props: {
        useIconAddress: {
            type: Boolean,
            default: false
        },
        zoom: {
            type: Number,
            default: 14
        },
        boundary: {
            type: Object
        },
        mapConfig: {
            type: Object,
            default: () => ({})
        },
        tools: {
            type: Array,
            default: () => []
        },
        center: {
            type: Object,
            default: () => null
        },
        autoCenter: {
            type: Boolean,
            default: false
        },
        address: {
            type: String,
            default: ''
        },
        makeAddressCenter: {
            type: Boolean,
            default: true
        },
        mapChangeEvent: {
            type: Array,
            default: () => []
        },
        markerData: {
            type: Object,
            default: () => ({})
        },
        markerEventMap: {
            type: Object,
            default: () => ({})
        }
    },
    data: function() {
        this.boundaries = [];
        this.ps = new Promise(resolve => {
            this.psResolve = resolve;
        });
        const defaultConfig = {
            pixelRatio: window.devicePixelRatio || 1,
            minZoom: 4,
            maxZoom: 20,
            fullscreenControl: !this.platform.isMobile,
            zoom: this.zoom || 14,
            center: this.center
        };
        const mapConfig = Object.assign({}, defaultConfig, this.mapConfig);
        if (mapConfig.zoom < mapConfig.minZoom) {
            mapConfig.zoom = mapConfig.minZoom;
        }
        if (mapConfig.zoom > mapConfig.maxZoom) {
            mapConfig.zoom = mapConfig.maxZoom;
        }
        return {
            aggregationMarkers: [],
            iconMarkers: [],
            houseMarkers: [],
            listingMarkers: [],
            schoolMarkers: [],
            cityMarkers: [],
            markerActiveMap: {
                aggre: '',
                house: '',
                school: '',
                location: '',
                business: '',
                schoolDetail: '',
                city: '',
                subway: ''
            },
            config: mapConfig,
            specialStyleManagement: {
                drawTip: {
                    openStatus: false
                }
            }, // Location style management for tool components
            opt_animate: true // Whether to apply animation transitions
        };
    },
    computed: {
        ...rootMapState(useUserStore, {
            userEmailVerified: 'userEmailVerified'
        }),
        ...rootMapState(usePageStore, {
            page: store => store.page,
            currencySymbol: store => store.currency.symbol
        }),
        ...mapState({
            drawing: 'drawing',
            mapCondition: 'mapCondition'
        }),
        isMobileSize() {
            return this.platform.isMobile;
        },
        calTools({ isMobileSize }) {
            let tools = [...this.tools];
            let exclusiveComps = ['mobileMaptype', 'nearby'];
            if (this.mapCondition?.mapRadius) {
                if (!isMobileSize) {
                    exclusiveComps.push('search');
                }
                tools = tools.filter(
                    item => !exclusiveComps.includes(item.name)
                );
            }
            if (this.config.fullscreenControl) {
                const toolsString = JSON.stringify(tools);
                if (toolsString.indexOf('fullscreen') === -1) {
                    const rightMatch = toolsString.match(/right:\s*(\d+)px/);
                    const topMath = toolsString.match(/top:\s*(\d+)px/);
                    tools.push({
                        name: 'fullscreen',
                        css:
                            'right: ' +
                            (rightMatch ? rightMatch[1] || 10 : 10) +
                            'px;top: ' +
                            (topMath ? topMath[1] || 10 : 10) +
                            'px;'
                    });
                }
            }
            if (this.config.gestureHandling !== 'greedy') {
                const toolsString = JSON.stringify(tools);
                if (toolsString.indexOf('map-mask') === -1) {
                    tools.push({
                        name: 'map-mask',
                        icon: 'map-mask',
                        css: ''
                    });
                }
            }
            return tools;
        },
        compCountry({ page }) {
            return page.country || 'usa';
        }
    },
    watch: {
        markerData: {
            deep: true,
            immediate: true,
            handler() {
                getMarkers.call(this);
            }
        },
        zoom(value) {
            this.setZoom(value);
        },
        center(value) {
            if (this.boundary) {
                return;
            }
            this.applyMap(() => {
                this.setCenter(value, this.zoom);
            });
        },
        address(value) {
            if (!this.center && this.makeAddressCenter) {
                Service.geocode(value).then(data => {
                    this.config.center = data.center;
                    this.applyMap(() => {
                        this.setCenter(data.center);
                    });
                });
            }
        },
        boundary: {
            immediate: true,
            handler: function(value) {
                if (value) {
                    this.applyMap(() => {
                        this.setCenter(null, value);
                    });
                }
            }
        }
    },
    beforeMount() {
        let ps = [util.getHereMapPs()];
        if (!this.config.center) {
            ps.push(
                Service.geocode(this.address, true).then(data => {
                    this.config.center = data.center;
                    if (data.zoom && !this.zoom) {
                        this.config.zoom = data.zoom;
                    }
                })
            );
        }
        this.preparePs = Promise.all(ps);
    },
    created() {
        this.setApplyMap(this.applyMap);
    },
    mounted() {
        this.preparePs.then(([maps]) => {
            const engineType = window.H.Map.EngineType['HARP'];
            this.baseLayer = maps.baseLayer;
            this.satelliteLayer = maps.satelliteLayer;
            const layer = maps.baseLayer;
            layer.setMin(this.config.minZoom); // The minimum degree to allow the map to zoom
            layer.setMax(this.config.maxZoom); // ditto

            // var provider = layer.getProvider();
            // // https://enterprise.here.com/map-style-editor/#11.401/52.54905/13.30446  online customization map style ,  Then download via service-tool upload to cdn
            // var style = new window.H.map.Style(
            //     'https://static.chimeroi.com/servicetool-temp/normal.day.yaml',
            //     'https://js.api.here.com/v3/3.1/styles/omv/miami/'
            // );
            // provider.setStyle(style);
            this.config.engineType = engineType;
            let map = new maps.H.Map(this.$refs.mapbox, layer, this.config);
            this.map = map;
            this.maps = maps;
            global.resize(this.mapResize);
            // 监听容器尺寸变化
            this.resizeObserver = new ResizeObserver(this.mapResize);
            this.resizeObserver.observe(this.$refs.mapbox);

            this.behavior = new maps.H.mapevents.Behavior(
                new maps.H.mapevents.MapEvents(map)
            );
            if (this.config.gestureHandling !== 'greedy') {
                this.behavior.disable(
                    maps.H.mapevents.Behavior.Feature.WHEEL_ZOOM
                );
                this.wheelEvent();
            }
            this.initMapEvent();
            let onceListener = () => {
                map.removeEventListener('mapviewchangeend', onceListener);
                onceListener = null;
                util.observeElementInViewport(
                    this.$refs.mapbox,
                    this.mapResize
                );
            };
            map.addEventListener('mapviewchangeend', onceListener);
        });
    },
    methods: {
        applyMap(callback) {
            return this.ps.then(callback);
        },
        setCenter(center, zoom, boundary, color) {
            if (!center) {
                center = this.center;
            }
            if (boundary) {
                const index = this.addBoundary(boundary, color, null);
                if (!boundary.noCenter) {
                    this.fitBoundary(index);
                }
                return index;
            }
            this.applyMap(([map]) => {
                center.lat = +center.lat;
                center.lng = +center.lng;
                map.setCenter(center);
                if (util.isObject(zoom)) {
                    zoom.data && this.clearBoundary();
                    zoom.data && this.addBoundary(zoom, color);
                    if (!zoom.noCenter) {
                        this.setZoom(zoom);
                    }
                    return;
                }
                if (util.isString(zoom) && zoom.indexOf('(') !== -1) {
                    this.clearBoundary();
                    var index = this.addBoundary(zoom, color, null);
                    // Adapt to boundary and set zoom
                    var bound = this.boundaries[index];
                    this.map.getViewModel().setLookAtData(
                        {
                            bounds: bound.getBoundingBox()
                        },
                        this.opt_animate
                    );
                    let onceListener = () => {
                        this.map.removeEventListener(
                            'mapviewchangeend',
                            onceListener
                        );
                        onceListener = null;
                        const mapZoom = Math.floor(this.map.getZoom());
                        this.map.setZoom(mapZoom, this.opt_animate);
                    };
                    this.map.addEventListener('mapviewchangeend', onceListener);
                    return;
                }
                if (!isNaN(zoom) && map.getZoom() !== zoom) {
                    map.setZoom(zoom, this.opt_animate);
                }
            });
        },
        /**
         *
         * @param {Number|Array} zoom
         * @param {Func} callback
         * @param {Boolean} reset  whether to reset zoom
         * @param {Boolean} opt_animate  Whether to enable transition animation
         */
        setZoom(zoom, callback, reset, opt_animate = true) {
            if (util.isObject(zoom)) {
                zoom = [
                    [zoom.lngMin, zoom.latMin],
                    [zoom.lngMin, zoom.latMax],
                    [zoom.lngMax, zoom.latMax],
                    [zoom.lngMax, zoom.latMin]
                ];
            }
            this.applyMap(([map, maps]) => {
                if (util.isArray(zoom)) {
                    const linestring = new maps.H.geo.LineString();
                    zoom.forEach(itemZoom => {
                        if (util.isArray(itemZoom)) {
                            linestring.pushPoint({
                                lng: itemZoom[0],
                                lat: itemZoom[1]
                            });
                        } else {
                            linestring.pushPoint(itemZoom);
                        }
                    });
                    // Scale according to bounds
                    map.getViewModel().setLookAtData(
                        {
                            bounds: linestring.getBoundingBox()
                        },
                        false
                    );
                    // OnceListener: after setLookAtData completes, set zoom again
                    let onceListener = () => {
                        map.removeEventListener(
                            'mapviewchangestart',
                            onceListener
                        );
                        onceListener = null;
                        const mapZoom = Math.floor(map.getZoom() - 0.2);
                        map.setZoom(mapZoom, opt_animate);
                        if (reset) {
                            this.config.zoom = mapZoom; // Reassignment overrides the zoom for locate button
                        }
                        util.call(callback, null, this);
                    };
                    map.addEventListener('mapviewchangestart', onceListener);
                } else {
                    map.setZoom((zoom = +zoom || 14), opt_animate);
                    util.call(callback, null, this);
                    // Cannot reassign config.zoom because it stores the initial zoom, after modification the locate button cannot return to initial zoom
                    // if (reset) {
                    //     this.config.zoom = zoom; // Reassignment overrides the zoom for locate button
                    // }
                }
            });
        },
        getBoundingBox() {
            return this.map
                .getViewModel()
                .getLookAtData()
                .bounds.getBoundingBox();
        },
        setActiveMarker(lat, lng, type) {
            this.markerActiveMap[type] = Latlng.format({
                lat,
                lng
            });
        },
        clearActiveMarker(type) {
            this.markerActiveMap[type] = '';
        },
        addBoundary(boundary, color, options = null) {
            if (!boundary) {
                return;
            }
            if (!color) {
                color = getBoundaryColor.call(this, this.mapTheme);
            }
            if (options && options.clear) {
                this.clearBoundary();
            }
            if (util.isString(color)) {
                color = {
                    strokeColor: util.colorToRgba(color, 0.8),
                    fillColor: util.colorToRgba(color, 0.2),
                    lineWidth: 2
                };
            } else {
                const newColor = {
                    strokeColor: util.colorToRgba(
                        color.strokeColor,
                        color.strokeOpacity || 0.8
                    ),
                    fillColor: util.colorToRgba(
                        color.fillColor,
                        color.fillOpacity || 0.2
                    ),
                    lineWidth: color.lineWidth || color.strokeWeight || 3
                };
                color = newColor;
            }
            let bound = null;
            if (
                util.isArray(boundary) ||
                (util.isObject(boundary) && !boundary.coordinates)
            ) {
                let boundaryData = [];
                let type = 'Polygon';

                if (util.isArray(boundary)) {
                    boundaryData = boundary;
                    let maxLength = 0;
                    boundaryData.forEach((itemBoundary, index) => {
                        if (itemBoundary.length > maxLength) {
                            maxLength = itemBoundary.length;
                            if (index !== 0) {
                                type = 'MultiPolygon';
                            }
                        }
                    });
                } else {
                    type = boundary.type || type;
                    boundaryData = boundary.data || [];
                }
                if (type === 'MultiPolygon') {
                    const geoPolygonArr = [];
                    boundaryData.forEach(boundDataItem => {
                        geoPolygonArr.push(addGeoPolygon(boundDataItem));
                    });
                    const multiPolygon = new window.H.geo.MultiPolygon(
                        geoPolygonArr
                    );
                    bound = new window.H.map.Polygon(multiPolygon, {
                        style: color
                    });
                } else {
                    bound = new window.H.map.Polygon(
                        addGeoPolygon(boundaryData),
                        {
                            style: color
                        }
                    );
                }
            } else {
                let wktData = '';
                if (util.isObject(boundary)) {
                    wktData = mapUtil.polygonToWKT(boundary);
                } else {
                    wktData = boundary;
                }
                const polygon = window.H.util.wkt.toGeometry(wktData);
                bound = new window.H.map.Polygon(polygon, {
                    style: color
                });
            }
            this.map.addObject(bound);
            this.boundaries.push(bound);
            initBoundaryListener(bound, options, color);
            return this.boundaries.length - 1;
        },
        fitBoundary(index, callback, animation) {
            var boundary = this.boundaries[index];
            this.map.getViewModel().setLookAtData({
                bounds: boundary.getBoundingBox()
            }, animation);
            let onceListener = () => {
                this.map.removeEventListener('mapviewchangeend', onceListener);
                onceListener = null;
                const mapZoom = Math.floor(this.map.getZoom());
                this.map.setZoom(mapZoom, this.opt_animate);
                if (callback) {
                    callback();
                }
            };
            this.map.addEventListener('mapviewchangeend', onceListener);
        },
        styleBoundary(idx, color) {
            const bound = this.boundaries[idx];
            if (!color) {
                color = getBoundaryColor.call(this, this.mapTheme);
            }
            if (bound) {
                if (util.isString(color)) {
                    color = {
                        strokeColor: util.colorToRgba(color, 0.8),
                        fillColor: util.colorToRgba(color, 0.2),
                        lineWidth: 2
                    };
                } else {
                    const newColor = {
                        strokeColor: util.colorToRgba(
                            color.strokeColor,
                            color.strokeOpacity || 0.8
                        ),
                        fillColor: util.colorToRgba(
                            color.fillColor,
                            color.fillOpacity || 0.2
                        ),
                        lineWidth: color.lineWidth || color.strokeWeight || 2
                    };
                    color = newColor;
                }
                bound.setStyle(color);
            }
        },
        clearBoundary(index) {
            if (index || index === 0) {
                const boundary = this.boundaries[index];
                if (!boundary) {
                    return;
                }
                this.applyMap(([map]) => {
                    map.removeObject(boundary);
                });
                this.boundaries.splice(index, 1);
                return;
            }
            for (let i = 0, len = this.boundaries.length; i < len; i++) {
                const boundary = this.boundaries[i];
                this.applyMap(([map]) => {
                    map.removeObject(boundary);
                });
            }
            this.boundaries.length = 0;
        },
        onMapPathChange(mapCondition) {
            if (mapCondition) {
                this.clearBoundary();
            }
            let key = 'mapPath';
            if (
                mapCondition &&
                Object.keys(mapCondition).includes('mapRadius')
            ) {
                key = 'mapRadius';
                mapCondition = mapCondition.mapRadius;
            }
            this.$emit('change', {
                [key]: mapCondition
            });
        },
        updateSchoolMarker(data) {
            if (!data) {
                this.schoolMarkers = [];
            } else {
                let list = Latlng.generateDataByLatlng(data);
                list.forEach(item => {
                    item.count = item.items.length;
                });
                this.schoolMarkers = list;
            }
        },
        disableMapChange() {
            this.disableMapChangeFlag = true;   
        },
        enableMapChange() {
            this.disableMapChangeFlag = false;
        },
        initMapEvent() {
            // Triggered when the visible area of the map changes
            let isFullScreen = false,
                status = '';
            this.map.addEventListener('mapviewchangeend', () => {
                if (this.disableMapChangeFlag) {
                    return;
                }
                // Track fullscreen button click event
                if (util.isFullScreen() !== isFullScreen) {
                    isFullScreen = !isFullScreen;
                    if (!isFullScreen && this.autoCenter) {
                        this.map.setCenter(this.center);
                    }
                    window.siteAppVm.fullscreenChange();
                    this.$EventBus.$emit('siteTrack', {
                        action: 'full-screen',
                        trackType: 'map-event',
                        ele: this.$el
                    });
                }
                if (status !== 'done') {
                    status = 'done';
                    if (this.platform.isMobile) {
                        const targetEle =
                            this.$el.querySelector('canvas') &&
                            this.$el.querySelector('canvas').nextSibling;
                        if (targetEle) {
                            targetEle.style.pointerEvents = 'none';
                            this.$refs.mapbox &&
                                this.$refs.mapbox.firstChild.appendChild(
                                    targetEle
                                );
                        }
                    }
                    this.psResolve([this.map, this.maps, this]);
                    this.$emit('mapLoad', this, this.map);
                }
                onMapChange.call(this);
            });
            // Track map drag event
            this.map.addEventListener('dragend', () => {
                this.$EventBus.$emit('siteTrack', {
                    action: 'drag',
                    trackType: 'map-event',
                    ele: this.$el
                });
            });
            // Double click on the map event
            this.map.addEventListener('dbltap', e => {
                let zoom = this.map.getZoom();
                let { viewportX, viewportY } = e.currentPointer;
                let point = this.map.screenToGeo(viewportX, viewportY);
                this.map.getViewModel().setLookAtData(
                    {
                        position: point,
                        zoom: zoom + 2
                    },
                    false
                );
            });
        },
        mapResize() {
            this.map.getViewPort().resize();
        },
        onMarkerReady(marker, idx) {
            if (marker === 'listing') {
                if (
                    this.listingMarkers[idx] &&
                    !this.listingMarkers[idx].destroyed
                ) {
                    for (let i = this.listingMarkers.length - 1; i >= 0; i--) {
                        if (this.listingMarkers[i].destroyed) {
                            this.listingMarkers.splice(0, i + 1);
                            break;
                        }
                    }
                }
            }
        },
        wheelEvent() {
            this.applyMap(() => {
                const canvasEle = this.$el.querySelector('canvas');
                const targetEle = canvasEle && canvasEle.parentNode;
                let zoomTimer = null;
                let isZooming = false;
                
                if (targetEle) {
                    targetEle.addEventListener('wheel', (e) => {
                        // Check if Cmd (Mac) or Ctrl (Windows) key is pressed for zoom
                        const isZoomGesture = e.metaKey || e.ctrlKey;
                        
                        if (isZoomGesture) {
                            // Prevent default behavior and zoom the map
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Debounce zoom control: one scroll triggers one zoom change
                            if (isZooming) {
                                return; // Ignore subsequent scrolls while zooming
                            }
                            
                            isZooming = true;
                            
                            // Clear previous timer
                            if (zoomTimer) {
                                clearTimeout(zoomTimer);
                                zoomTimer = null;
                            }
                            
                            // Determine scroll direction: deltaY > 0 is down (zoom out), < 0 is up (zoom in)
                            const direction = e.deltaY > 0 ? -1 : 1;
                            
                            // Calculate new zoom level
                            const currentZoom = Math.round(this.map.getZoom());
                            const newZoom = currentZoom + direction;
                            // Check zoom range
                            if (newZoom >= this.config.minZoom && newZoom <= this.config.maxZoom) {
                                // Use setZoom method for unified handling and state consistency
                                this.setZoom(newZoom, null, false, this.opt_animate);
                            }
                            
                            // Set debounce delay: 300ms before allowing next zoom trigger
                            zoomTimer = setTimeout(() => {
                                isZooming = false;
                            }, 300);
                        }
                        // If no modifier key is pressed, allow default page scroll behavior
                        // (do not call e.preventDefault())
                    }, { passive: false });
                }
            });
        },
        // Save current zoom and center as initial positioning coordinates for toolbar
        saveAsDefaultLocate() {
            this.config.center = this.map.getCenter();
            this.config.zoom = Math.round(this.map.getZoom());
        },
        handlerCss(css, changeStyle) {
            if (!changeStyle) {
                return css;
            }
            let cssObj = {}; // Convert CSS string to object
            css.split(';').forEach(item => {
                if (item) {
                    let [k, v] = item.split(':');
                    cssObj[k.trim()] = v.trim();
                }
            });
            Object.assign(cssObj, {
                top:
                    Number.parseInt(cssObj.top) +
                    (this.specialStyleManagement.drawTip.openStatus ? 40 : 10) +
                    'px'
            });
            return cssObj;
        },
        fitMarker(allMarkers) {
            // const maxLat = Math.max(...allMarkers.map(item=>item.lat))
            // const maxLng = Math.max(...allMarkers.map(item=>item.lng))
            // const minLat = Math.min(...allMarkers.map(item=>item.lat))
            // const minLng = Math.min(...allMarkers.map(item=>item.lng))
            // new Promise(resolve=>{
            //     this.fitBoundaryPsResolve = resolve
            // }).then(()=>{
            //     const boundingBox = new window.H.geo.Rect(maxLat,maxLng,minLat,minLng);
            //     this.map.getViewModel().setLookAtData({
            //         bounds: boundingBox.getBoundingBox()
            //     });
            // })
        },
    },
    unmounted() {
        global.resize(this.mapResize, true);
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
    components: {
        ToolMapType,
        ToolZoom,
        Nearby,
        Locate,
        Fullscreen,
        Street,
        Search,
        Icon,
        MarkerAggre,
        MarkerIcon,
        MarkerHouse,
        MarkerHouseBroker,
        MarkerHouseLayout,
        MarkerSchool,
        MarkerSchoolLayout,
        MarkerCity,
        MapMask,
        School: defineAsyncComponent(() =>
            import(
                /* webpackChunkName: "map/tools-school"*/ '../../components/map/tools/school.vue'
            )),
        Draw: defineAsyncComponent(() =>
            import(/* webpackChunkName: "map/tools-draw"*/ './tools/draw.vue'))
    }
};


function onMapChange() {
    // util.timeout(onMapChange, true);
    this.$emit('change', getData(this.map, this));
    for (let callback of this.mapChangeEvent) {
        util.call(callback, this);
    }
}

function getData(map, siteMap) {
    const data = {
        zoom: Math.round(map.getZoom())
    };
    const box = siteMap.getBoundingBox();
    if (box) {
        data.rect =
            box.getTop() +
            ',' +
            box.getLeft() +
            ',' +
            box.getBottom() +
            ',' +
            box.getRight();
    }
    return data;
}

function addGeoPolygon(boundaryData) {
    const linestring = new window.H.geo.LineString();
    const linestringArr = [];
    boundaryData.forEach((itemBoundary, index) => {
        if (
            util.isArray(itemBoundary) &&
            (util.isObject(itemBoundary[0]) || util.isArray(itemBoundary[0]))
        ) {
            if (index === 0) {
                itemBoundary.forEach(point => {
                    linestring.pushPoint(
                        util.isArray(point)
                            ? {
                                lng: point[0],
                                lat: point[1]
                            }
                            : point
                    );
                });
            } else {
                const itemLine = new window.H.geo.LineString();
                itemBoundary.forEach(point => {
                    itemLine.pushPoint(
                        util.isArray(point)
                            ? {
                                lng: point[0],
                                lat: point[1]
                            }
                            : point
                    );
                });
                linestringArr.push(itemLine);
            }
        } else {
            linestring.pushPoint(
                util.isArray(itemBoundary)
                    ? {
                        lng: itemBoundary[0],
                        lat: itemBoundary[1]
                    }
                    : itemBoundary
            );
        }
    });
    const geoPolygon = new window.H.geo.Polygon(linestring, linestringArr);
    return geoPolygon;
}

function initBoundaryListener(bound, options, color) {
    if (options) {
        let listeners = options.listeners || {},
            hcolor = options.hover,
            link = options.link;
        if (hcolor) {
            if (util.isString(hcolor)) {
                hcolor = Object.assign({}, color, {
                    fillColor: util.colorToRgba(hcolor, 0.8),
                    strokeColor: util.colorToRgba(hcolor, 0.2)
                });
            } else {
                const newHcolor = {
                    strokeColor: util.colorToRgba(
                        hcolor.strokeColor,
                        hcolor.strokeOpacity || 0.8
                    ),
                    fillColor: util.colorToRgba(
                        hcolor.fillColor,
                        hcolor.fillOpacity || 0.2
                    ),
                    lineWidth:
                        hcolor.lineWidth ||
                        hcolor.strokeWeight ||
                        color.lineWidth
                };
                hcolor = newHcolor;
            }
            listeners.pointerenter = function() {
                bound.setStyle(hcolor);
                util.call(options.hover, bound);
            };
            listeners.pointerleave = function() {
                bound.setStyle(color);
            };
        }
        if (link) {
            listeners.tap = function() {
                window.location.href = link;
            };
        }
        for (let name in listeners) {
            bound.addEventListener(name, listeners[name]);
        }
    }
}
